import { useCallback, useMemo } from "react";
import type {
  BillingPlanSummary,
  DashboardSidebarNavItem,
  DashboardUsageMetric,
  PlanTier,
} from "../types/billing";

interface UseDashboardSidebarOptions {
  planSummary: BillingPlanSummary | null;
}

const DASHBOARD_NAV_ITEMS: DashboardSidebarNavItem[] = [
  // {
  //   id: "overview",
  //   label: "Overview",
  //   sectionId: "dashboard-overview",
  //   icon: "overview",
  // },
  // {
  //   id: "brands",
  //   label: "Your brands",
  //   sectionId: "dashboard-brands",
  //   icon: "brands",
  // },
  // {
  //   id: "history",
  //   label: "Recent generations",
  //   sectionId: "dashboard-generations",
  //   icon: "history",
  // },
];

const PRO_HIGHLIGHTS = [
  "3 brands on Pro",
  "10 brands on Agency",
  "Scheduling + SEO",
];

const getPlanLabel = (planTier: PlanTier) => {
  if (planTier === "agency") {
    return "Agency";
  }
  if (planTier === "pro") {
    return "Pro";
  }
  return "Free";
};

const getUsageProgress = (used: number, limit: number | null): number => {
  if (limit == null || limit <= 0) {
    return 0;
  }

  return Math.min(100, Math.round((used / limit) * 100));
};

const useDashboardSidebar = ({
  planSummary,
}: UseDashboardSidebarOptions) => {
  const handleNavClick = useCallback((sectionId: string) => {
    if (typeof document === "undefined") {
      return;
    }

    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const usageMetrics = useMemo<DashboardUsageMetric[]>(
    () => {
      const planTier = planSummary?.planTier ?? "free";
      const betaEnabled = Boolean(planSummary?.betaEnabled);
      const betaLabel = planSummary?.betaLabel ?? "Free beta";
      const brandLimit = planSummary?.limits.brands ?? null;
      const usedBrands = planSummary?.usage.brands ?? 0;
      const generationLimit = planSummary?.limits.monthlyGenerations ?? null;
      const usedGenerations = planSummary?.usage.monthlyGenerations ?? 0;

      return [
        {
          id: "brands",
          label: "Brands used",
          used: usedBrands,
          limit: brandLimit,
          limitLabel: brandLimit == null ? "Unlimited" : String(brandLimit),
          progress: getUsageProgress(usedBrands, brandLimit),
          helper:
            betaEnabled
              ? `${usedBrands} brands active during ${betaLabel.toLowerCase()}`
              : `${usedBrands} of ${
                  brandLimit ?? "Unlimited"
                } brands on ${getPlanLabel(planTier)}`,
        },
        {
          id: "generations",
          label: "Generations used",
          used: usedGenerations,
          limit: generationLimit,
          limitLabel:
            generationLimit == null ? "Unlimited" : String(generationLimit),
          progress: getUsageProgress(usedGenerations, generationLimit),
          helper:
            betaEnabled
              ? `${usedGenerations} generations used during ${betaLabel.toLowerCase()}`
              : generationLimit == null
              ? `${usedGenerations} monthly generations on ${getPlanLabel(planTier)}`
              : `${usedGenerations} of ${generationLimit} monthly generations`,
        },
      ];
    },
    [planSummary],
  );

  return useMemo(
    () => ({
      planName:
        planSummary?.betaLabel ??
        getPlanLabel((planSummary?.planTier ?? "free") as PlanTier),
      betaEnabled: Boolean(planSummary?.betaEnabled),
      betaLabel: planSummary?.betaLabel ?? null,
      usageMetrics,
      navItems: DASHBOARD_NAV_ITEMS,
      handleNavClick,
      proHighlights: PRO_HIGHLIGHTS,
    }),
    [
      handleNavClick,
      planSummary?.betaEnabled,
      planSummary?.betaLabel,
      planSummary?.planTier,
      usageMetrics,
    ],
  );
};

export default useDashboardSidebar;
